import { memo, useCallback, useState } from 'react';

import { ChevronDownIcon, ChevronRightIcon, PauseIcon, PlayIcon, XIcon } from '@air/design-icons';

import { FileStatus } from '../../constants/FileStatus';
import { FileStatusButton } from './FileStatusButton';

export type FileStatusPanelHeaderButtonsProps = {
  onResume?: () => Promise<void> | void;
  onCancel?: () => void;
  onPause?: () => void;
  onDismiss?: () => void;
  status?: FileStatus;
  isUploaderOpen: boolean;
  onToggle?: (isOpen: boolean) => void;
  disableCancel?: boolean;
  disableDismiss?: boolean;
};

export const FileStatusPanelHeaderButtons = memo(
  ({
    onDismiss,
    onPause,
    onResume,
    status,
    onCancel,
    isUploaderOpen,
    onToggle,
    disableCancel,
    disableDismiss,
  }: FileStatusPanelHeaderButtonsProps) => {
    const [isResuming, setIsResuming] = useState(false);

    const handleResume = useCallback(async () => {
      setIsResuming(true);
      try {
        await onResume?.();
      } finally {
        setIsResuming(false);
      }
    }, [onResume]);

    return (
      <>
        {onPause && status === FileStatus.progressing && (
          <FileStatusButton label="Pause" onClick={onPause}>
            <PauseIcon size="sm" />
          </FileStatusButton>
        )}

        {onResume && (status === FileStatus.stopped || status === FileStatus.paused) && (
          <FileStatusButton isLoading={isResuming} label="Resume" onClick={handleResume}>
            <PlayIcon size="sm" />
          </FileStatusButton>
        )}

        {onDismiss && !disableDismiss && (disableCancel || status === FileStatus.completed) && (
          <FileStatusButton data-testid="CLOSE_UPLOADS_BUTTON" label="Close" onClick={onDismiss}>
            <XIcon size="sm" />
          </FileStatusButton>
        )}

        {onCancel && status !== FileStatus.completed && (
          <FileStatusButton data-testid="CANCEL_UPLOADS_BUTTON" label="Cancel" onClick={onCancel}>
            <XIcon size="sm" />
          </FileStatusButton>
        )}
        {onToggle &&
          (isUploaderOpen ? (
            // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
            <FileStatusButton appearance="ghost" label="Minimize" onClick={() => onToggle(false)}>
              <ChevronDownIcon size="sm" />
            </FileStatusButton>
          ) : (
            // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
            <FileStatusButton appearance="ghost" label="Expand" onClick={() => onToggle(true)}>
              <ChevronRightIcon size="sm" />
            </FileStatusButton>
          ))}
      </>
    );
  },
);

FileStatusPanelHeaderButtons.displayName = 'FileStatusPanelHeaderButtons';
